// Página de estadísticas con gráficos interactivos del mundial
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3,
  Trophy,
  Target,
  Star,
  TrendingUp,
  Users,
  Percent,
  RefreshCw,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  Legend,
} from 'recharts'
import client from '../api/client'
import Card from '../components/ui/Card'

// Colores para los gráficos
const CHART_COLORS = [
  '#00C2FF', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1',
  '#D946EF', '#0EA5E9',
]

// Tooltip personalizado para los gráficos
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="glass-strong rounded-lg px-4 py-3 text-sm shadow-lg">
      <p className="font-display font-semibold text-text-primary mb-1">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-text-secondary">
          {entry.name}: <span className="font-mono text-cyan font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  )
}

// Tarjeta de estadística individual
function StatCard({ icon: Icon, label, value, color = 'cyan', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
    >
      <Card className="p-5">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
          <div>
            <p className="text-text-muted text-sm">{label}</p>
            <p className="font-display font-bold text-2xl text-text-primary countdown-digit">
              {value}
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  )
}

// Datos de demostración cuando la API no está disponible
const demoData = {
  total_usuarios: 1284,
  total_predicciones: 956,
  total_ligas: 47,
  equipos_campeon: [
    { nombre: 'Argentina', votos: 245 },
    { nombre: 'Brasil', votos: 198 },
    { nombre: 'Francia', votos: 176 },
    { nombre: 'Inglaterra', votos: 112 },
    { nombre: 'España', votos: 89 },
    { nombre: 'Alemania', votos: 67 },
    { nombre: 'Portugal', votos: 45 },
    { nombre: 'Países Bajos', votos: 24 },
  ],
  goleadores: [
    { nombre: 'K. Mbappé', votos: 312 },
    { nombre: 'E. Haaland', votos: 245 },
    { nombre: 'L. Messi', votos: 189 },
    { nombre: 'V. Osimhen', votos: 134 },
    { nombre: 'H. Kane', votos: 76 },
  ],
  mejores_jugadores: [
    { nombre: 'L. Messi', votos: 287 },
    { nombre: 'K. Mbappé', votos: 256 },
    { nombre: 'J. Bellingham', votos: 198 },
    { nombre: 'Vinícius Jr.', votos: 123 },
    { nombre: 'R. Lewandowski', votos: 92 },
  ],
  paises_usuarios: [
    { nombre: 'México', usuarios: 312 },
    { nombre: 'Argentina', usuarios: 245 },
    { nombre: 'Colombia', usuarios: 189 },
    { nombre: 'España', usuarios: 134 },
    { nombre: 'Chile', usuarios: 112 },
    { nombre: 'Perú', usuarios: 98 },
    { nombre: 'Ecuador', usuarios: 76 },
    { nombre: 'Venezuela', usuarios: 54 },
  ],
}

export default function Stats() {
  const [activeTab, setActiveTab] = useState('campeon')

  // Obtener estadísticas de la API
  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['stats-general'],
    queryFn: async () => {
      try {
        const response = await client.get('/stats/general')
        return response.data.datos
      } catch {
        // Retornar datos de demostración si la API no está disponible
        return demoData
      }
    },
    staleTime: 60000,
  })

  const data = stats || demoData

  // Pestañas de estadísticas
  const tabs = [
    { id: 'campeon', label: 'Campeón', icon: Trophy },
    { id: 'goleador', label: 'Goleador', icon: Target },
    { id: 'jugador', label: 'Mejor Jugador', icon: Star },
    { id: 'paises', label: 'Países', icon: Users },
  ]

  // Datos del gráfico según la pestaña activa
  const getChartData = () => {
    switch (activeTab) {
      case 'campeon':
        return data.equipos_campeon || []
      case 'goleador':
        return data.goleadores || []
      case 'jugador':
        return data.mejores_jugadores || []
      case 'paises':
        return data.paises_usuarios || []
      default:
        return []
    }
  }

  const chartData = getChartData()
  const chartKey = activeTab === 'paises' ? 'usuarios' : 'votos'

  // Calcular el total para porcentajes
  const totalVotos = chartData.reduce((sum, item) => sum + (item[chartKey] || 0), 0)

  // Datos para el gráfico circular
  const pieData = chartData.slice(0, 6).map((item, idx) => ({
    name: item.nombre,
    value: item[chartKey],
    fill: CHART_COLORS[idx],
  }))

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4"
      >
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-text-primary">
            Estadísticas
          </h1>
          <p className="text-text-secondary mt-1">
            Tendencias y datos globales de las predicciones
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-cyan hover:text-cyan-light transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar datos
        </button>
      </motion.div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Users}
          label="Usuarios"
          value={data.total_usuarios?.toLocaleString() || '0'}
          color="cyan"
          delay={0}
        />
        <StatCard
          icon={BarChart3}
          label="Predicciones"
          value={data.total_predicciones?.toLocaleString() || '0'}
          color="success"
          delay={0.1}
        />
        <StatCard
          icon={Trophy}
          label="Comunidades Activas"
          value={data.total_ligas?.toLocaleString() || '0'}
          color="warning"
          delay={0.2}
        />
        <StatCard
          icon={Percent}
          label="Participación"
          value={
            data.total_usuarios
              ? `${Math.round((data.total_predicciones / data.total_usuarios) * 100)}%`
              : '0%'
          }
          color="error"
          delay={0.3}
        />
      </div>

      {/* Pestañas de navegación */}
      <div className="flex gap-1 p-1 glass rounded-xl mb-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-display font-medium
              transition-all duration-200 whitespace-nowrap flex-1 justify-center
              ${activeTab === tab.id
                ? 'bg-cyan text-navy-dark shadow-cyan'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-card'
              }
            `}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenido de gráficos */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Gráfico de barras principal */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="lg:col-span-3"
        >
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-cyan" />
              <h2 className="font-display font-semibold text-lg text-text-primary">
                {activeTab === 'campeon' && 'Campeón más votado'}
                {activeTab === 'goleador' && 'Máximo goleador más votado'}
                {activeTab === 'jugador' && 'Mejor jugador más votado'}
                {activeTab === 'paises' && 'Usuarios por país'}
              </h2>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" tick={{ fill: '#94A3B8', fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="nombre"
                    tick={{ fill: '#F1F5F9', fontSize: 13, fontFamily: 'Plus Jakarta Sans' }}
                    width={120}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey={chartKey}
                    name={activeTab === 'paises' ? 'Usuarios' : 'Votos'}
                    radius={[0, 6, 6, 0]}
                  >
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Gráfico circular + lista */}
        <motion.div
          key={`pie-${activeTab}`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="lg:col-span-2 flex flex-col gap-6"
        >
          {/* Distribución circular */}
          <Card className="p-6">
            <h3 className="font-display font-semibold text-text-primary mb-4">Distribución</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`pie-cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Ranking de la pestaña activa */}
          <Card className="p-6 flex-1">
            <h3 className="font-display font-semibold text-text-primary mb-4">Ranking</h3>
            <div className="space-y-3">
              {chartData.slice(0, 5).map((item, index) => {
                const percent = totalVotos > 0
                  ? Math.round((item[chartKey] / totalVotos) * 100)
                  : 0

                return (
                  <div key={item.nombre} className="flex items-center gap-3">
                    <span
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-mono font-bold"
                      style={{ backgroundColor: CHART_COLORS[index] + '20', color: CHART_COLORS[index] }}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-text-primary truncate">{item.nombre}</span>
                        <span className="text-xs font-mono text-text-muted ml-2">{percent}%</span>
                      </div>
                      <div className="h-1.5 bg-surface-card rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: CHART_COLORS[index] }}
                          initial={{ width: 0 }}
                          animate={{ width: `${percent}%` }}
                          transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
